import React, { useState } from "react";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { FrontQuestionFormCard } from "../../components/Front/Complex/FrontQuestionFormCard/FrontQuestionFormCard";
import FrontParagraphSmall from "../../components/Front/Simple/FrontParagraphSmall/FrontParagraphSmall";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { FrontSpacing } from "../../models/FrontSpacing";
import { WeightSemiBold } from "../../components/WeightSemiBold/WeightSemiBold";
import { WeightMedium } from "../../components/WeightMedium/WeightMedium";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { TOnboardingTabStatus } from "../../components/Front/Simple/FrontOnboardingTab/FrontOnboardingTab";
import FrontInputLabelAndErrorWrapComponent from "../../components/Front/Simple/FrontInputLabelAndErrorWrapComponent/FrontInputLabelAndErrorWrapComponent";
import { FrontRadioList } from "../../components/Front/Simple/FrontRadioList/FrontRadioList";
import { FrontFormPrimaryButton } from "../../components/Front/Simple/FrontFormPrimaryButton/FrontFormPrimaryButton";
import { FrontInfoBubble } from "../../components/Front/Simple/FrontInfoBubble/FrontInfoBubble";
import { FrontColors } from "../../models/FrontColors";
import { ClientCaseEpcWhoArrangesForm } from "../../../../domain/codecs/form/CaseEpcForm";
import { TCasesEpcWhoArranges } from "../../../../domain/codecs/CasesEpc";

const FrontEpcWhoArrangesContainer = (props: TContainerStateProps): JSX.Element => {
    const epcStep = props.state.forms.client_case_page.children.epc_who_arranges;
    const form = epcStep?.form || ClientCaseEpcWhoArrangesForm.newDefault();

    const isStepComplete = epcStep ? epcStep.is_step_complete : true;

    const [displayValidationErrorsIfPresent, setDisplayValidationErrorsIfPresent] = useState<boolean>(false);

    const value = form.edited.who_arranges_new_epc === "unknown" ? null : form.edited.who_arranges_new_epc;

    const onChange = (who_arranges_new_epc: TCasesEpcWhoArranges) =>
        props.dispatch({
            type: "CLIENT_EPC_WHO_ARRANGES_CHANGE",
            payload: {
                ...form,
                edited: { ...form.edited, who_arranges_new_epc },
            },
        });

    const onSubmit = () =>
        value !== null
            ? props.dispatch({ type: "CLIENT_EPC_WHO_ARRANGES_SUBMIT", payload: form })
            : setDisplayValidationErrorsIfPresent(true);

    return <>
        {!isStepComplete &&
            <FrontQuestionFormCard title="Energy Performance Certificate">
                <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                    <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                        <FrontParagraphSmall>
                            We couldn't find a valid Energy Performance Certificate (EPC) for your property.&nbsp;
                            <WeightSemiBold>A valid EPC is needed before your sale can complete.</WeightSemiBold>
                        </FrontParagraphSmall>
                        <FrontParagraphSmall>
                            <WeightMedium>We're happy to sort a new one for you, or you can arrange it yourself.</WeightMedium>
                        </FrontParagraphSmall>
                    </SpacingColumn>

                    <FrontInputLabelAndErrorWrapComponent label={<WeightMedium>Who'll arrange the new EPC?</WeightMedium>}>
                        <FrontRadioList
                            value={value}
                            options={[
                                { value: "us", label: "Sail Legal — please sort it for me" },
                                { value: "client", label: "I'll arrange it myself" },
                            ]}
                            onChange={onChange}
                        />
                    </FrontInputLabelAndErrorWrapComponent>

                    <FrontFormPrimaryButton
                        label="Continue"
                        icon="done"
                        isDisabled={false}
                        onClick={onSubmit}
                    />

                    {displayValidationErrorsIfPresent && value === null &&
                        <FrontInfoBubble color={FrontColors.ERROR_FIREBRICK_22}>
                            <WeightBold>Please choose an option</WeightBold> before continuing.
                        </FrontInfoBubble>
                    }
                </SpacingColumn>
            </FrontQuestionFormCard>
        }

        {isStepComplete &&
            <FrontQuestionFormCard title="Energy Performance Certificate">
                <FrontParagraphSmall>
                    <WeightSemiBold>Thanks — we've noted who'll arrange the new EPC.</WeightSemiBold>
                </FrontParagraphSmall>
            </FrontQuestionFormCard>
        }
    </>;
};

export const getEpcWhoArrangesStatus = (props: TContainerStateProps): TOnboardingTabStatus =>
    props.state.forms.client_case_page.children.epc_who_arranges?.is_step_complete
        ? "completed"
        : "active";

export default FrontEpcWhoArrangesContainer;
